import React, { useState, useEffect, useRef } from 'react';
import { Table, Typography, Select, DatePicker, Space, Empty } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, SyncOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  getHistorico,
  type BrevoSyncHistoricoItem,
  type HistoricoFilters,
  formatDuracion,
  fmtFechaHoraMadrid,
  fmtFechaHoraSegundosMadrid,
} from '../../api/brevoSyncApi';
import { FctEstado, fmtNumero, type FctFamilia } from '../../fct/react';
import { fct } from '../../fct/tokens/tokens';

const { Text } = Typography;
const { RangePicker } = DatePicker;

interface HistorySectionProps {
  /** Cambia cuando cambia la ejecución viva o la última acabada (huellaHistorico): el listado se pone al día solo (#247). */
  huella?: string;
}

const HistorySection: React.FC<HistorySectionProps> = ({ huella }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<BrevoSyncHistoricoItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [estadoFilter, setEstadoFilter] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

  // silencioso: al ponerse al día con el estado, sin el indicador de carga, para que la tabla no parpadee.
  const loadData = async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    try {
      const filters: HistoricoFilters = {
        page,
        pageSize,
        tipo: 'diario',
        estado: estadoFilter,
        desde: dateRange?.[0]?.format('YYYY-MM-DD'),
        hasta: dateRange?.[1]?.format('YYYY-MM-DD'),
      };
      const result = await getHistorico(filters);
      setData(result.ejecuciones);
      setTotal(result.total);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [page, pageSize, estadoFilter, dateRange]);

  // Al cambiar la huella (lanzar, avanzar, acabar), el listado se vuelve a leer; la primera la cubre la carga inicial.
  const huellaVista = useRef(huella);
  const cargar = useRef(loadData);
  cargar.current = loadData;
  useEffect(() => {
    if (huella === huellaVista.current) return;
    huellaVista.current = huella;
    cargar.current(true);
  }, [huella]);

  // Icono en una tinta (R-14): el estado lo dice la pastilla de al lado, con color, icono y etiqueta.
  const estadoIcon = (estado: string) => {
    switch (estado) {
      case 'ok': return <CheckCircleOutlined />;
      case 'error': return <CloseCircleOutlined />;
      case 'running': return <SyncOutlined spin />;
      default: return null;
    }
  };

  const estadoTag = (estado: string, item: BrevoSyncHistoricoItem) => {
    if (estado === 'ok' && (item.faseAErrores > 0 || item.faseBErrores > 0)) {
      return <FctEstado personalizado={{ etiqueta: `${item.faseAErrores + item.faseBErrores} errores`, familia: 'aviso', icono: 'alerta' }} />;
    }
    const map: Record<string, { familia: FctFamilia; label: string }> = {
      ok: { familia: 'exito', label: 'Correcta' },
      error: { familia: 'riesgo', label: 'Fallida' },
      running: { familia: 'proceso', label: 'En curso' },
      pendiente: { familia: 'proceso', label: 'Pendiente' },
      cancelada: { familia: 'neutro', label: 'Parada' },
    };
    const cfg = map[estado] || { familia: 'neutro', label: estado };
    return <FctEstado personalizado={{ etiqueta: cfg.label, familia: cfg.familia }} />;
  };

  const columns: ColumnsType<BrevoSyncHistoricoItem> = [
    {
      title: 'Fecha',
      dataIndex: 'inicio',
      key: 'fecha',
      width: 150,
      className: 'fct-num',
      align: 'right',
      render: (val: string) => fmtFechaHoraMadrid(val),
    },
    {
      title: '',
      key: 'icon',
      width: 30,
      render: (_, r) => estadoIcon(r.estado),
    },
    {
      title: 'Resultado',
      key: 'resultado',
      width: 180,
      render: (_, r) => {
        if (r.estado === 'running' || r.estado === 'pendiente') return estadoTag(r.estado, r);
        const total = r.faseAExitos + r.faseBExitos;
        return <span><span className="fct-mono">{fmtNumero(total)}</span> contactos {estadoTag(r.estado, r)}</span>;
      },
    },
    {
      title: 'Duración',
      dataIndex: 'duracionSegundos',
      key: 'duracion',
      width: 100,
      className: 'fct-num',
      align: 'right',
      render: (val: number) => formatDuracion(val),
    },
    {
      title: 'Origen',
      dataIndex: 'origen',
      key: 'origen',
      width: 100,
      render: (val: string) => <Text type="secondary" style={{ textTransform: 'capitalize' }}>{val}</Text>,
    },
  ];

  const expandedRowRender = (record: BrevoSyncHistoricoItem) => (
    <div style={{ padding: '8px 0' }}>
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Text><strong>Inicio:</strong> <span className="fct-mono">{fmtFechaHoraSegundosMadrid(record.inicio)}</span></Text>
        {record.fin && <Text><strong>Fin:</strong> <span className="fct-mono">{fmtFechaHoraSegundosMadrid(record.fin)}</span></Text>}
        <Text><strong>Fase A:</strong> <span className="fct-mono">{fmtNumero(record.faseAExitos)}</span> OK, <span className="fct-mono">{fmtNumero(record.faseAErrores)}</span> errores{record.faseATotal ? <> de <span className="fct-mono">{fmtNumero(record.faseATotal)}</span></> : ''}</Text>
        <Text><strong>Fase B:</strong> <span className="fct-mono">{fmtNumero(record.faseBExitos)}</span> OK, <span className="fct-mono">{fmtNumero(record.faseBErrores)}</span> errores{record.faseBTotal ? <> de <span className="fct-mono">{fmtNumero(record.faseBTotal)}</span></> : ''}</Text>
        <Text><strong>Duplicados fusionados:</strong> <span className="fct-mono">{fmtNumero(record.duplicadosFusionados)}</span></Text>
        {record.parametros && <Text><strong>Parámetros:</strong> {record.parametros}</Text>}
        {record.errorDetalle && (
          <div style={{ background: fct.estado.riesgo.relleno, padding: '8px 12px', borderRadius: 4, marginTop: 4 }}>
            <Text type="danger" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{record.errorDetalle}</Text>
          </div>
        )}
      </Space>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Text strong style={{ fontSize: 14 }}>Histórico de sincronizaciones</Text>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <RangePicker
          value={dateRange}
          onChange={(dates) => { setDateRange(dates); setPage(1); }}
          format="DD/MM/YYYY"
          placeholder={['Desde', 'Hasta']}
          size="small"
        />
        <Select
          value={estadoFilter}
          onChange={(val) => { setEstadoFilter(val); setPage(1); }}
          placeholder="Estado"
          allowClear
          size="small"
          style={{ width: 140 }}
          options={[
            { label: 'Correctas', value: 'ok' },
            { label: 'Fallidas', value: 'error' },
            { label: 'En curso', value: 'running' },
            { label: 'Paradas', value: 'cancelada' },
          ]}
        />
      </div>
      <Table
        dataSource={data.map(d => ({ ...d, key: d.id }))}
        columns={columns}
        loading={loading}
        expandable={{ expandedRowRender }}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          showSizeChanger: true,
          pageSizeOptions: ['10', '30', '50'],
          showTotal: (t) => `${t} ejecuciones`,
          size: 'small',
        }}
        size="small"
        locale={{ emptyText: <Empty description="Sin ejecuciones registradas" /> }}
      />
    </div>
  );
};

export default HistorySection;
